/**
 * ============================================================================
 * KnowledgeProtectedRoute — Guarda das rotas /tenant/kb*
 * ----------------------------------------------------------------------------
 * Bloqueia:
 *   1) Tenants com o módulo Base de Conhecimento desativado
 *      (`tenants.knowledge_enabled = false`) — preparação de add-on futuro.
 *   2) Usuários sem `kb_view` no tenant atual.
 * Em ambos os casos redireciona para /tenant/dashboard.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.1.0 - Passa a respeitar a flag knowledge_enabled do
 *            tenant antes da checagem de permissão granular.
 *   RELEASE: 2026-07-07 v1.0.0 - F1-Fix. Guard inicial.
 * ============================================================================
 */
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useKnowledgePermissions } from "@/hooks/kb/useKnowledgePermissions";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  children: ReactNode;
}

export function KnowledgeProtectedRoute({ children }: Props) {
  const { loading, canView } = useKnowledgePermissions();
  const { currentTenant } = useAuth();

  if (loading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Gate 1: módulo desabilitado para o tenant (add-on inativo)
  const knowledgeEnabled =
    (currentTenant as { knowledge_enabled?: boolean } | null)?.knowledge_enabled !== false;
  if (!knowledgeEnabled) {
    return <Navigate to="/tenant/dashboard" replace />;
  }

  // Gate 2: permissão granular do usuário
  if (!canView) {
    return <Navigate to="/tenant/dashboard" replace />;
  }

  return <>{children}</>;
}
